<script setup lang="ts">
import { ChevronLeftIcon } from "@heroicons/vue/solid"
import Logo from "@/assets/xypn-logo.svg"
import { getBaseNavigation, useUserNavigation } from "@/composables/navigation"
import BenchmarkingBanner from "@/components/benchmarking/TopBanner.vue"
import ReferralCodeTile from "@/components/shared/ReferralCodeTile.vue"
import SidebarTools from "@/components/tools/SidebarTools.vue"
import SidebarLayout from "@/components/shared/SidebarLayout.vue"

const props = defineProps<{
  title: string
  /**
   * returnLink provides a navigation arrow in the title bar
   * this is best used when the active page is not directly linked to
   * in any of the apps primary navigation points.
   *
   * ex: inidividual benefit pages, announcements, IR Progress
   */
  returnLink?: string
}>()

const initialProps = window.InitialVueProps.initialProps
const currentUser = window.InitialVueProps.initialProps.currentUser
const toolbox = window.InitialVueProps.toolbox || []
const appProps = window.InitialVueProps.appProps

const baseNavigation = getBaseNavigation(currentUser)
const userNavigation = useUserNavigation(currentUser)
const currentPath = window.location.pathname || "/"
</script>

<template>
  <SidebarLayout
    :active-url="currentPath"
    :icon-url="Logo"
    :navigation="baseNavigation"
    :user-navigation="userNavigation"
  >
    <template #header>
      <div class="flex items-center space-x-1.5">
        <a v-if="returnLink" :href="returnLink" class="block p-2">
          <ChevronLeftIcon class="w-6 h-6" />
        </a>
        <div class="truncate">{{ props.title }}</div>
      </div>
    </template>

    <template #default>
      <BenchmarkingBanner v-if="appProps.showBenchmarking" />
      <!-- NOTE(spk): extra padding on the bottom helps accommodate the helpscout button -->
      <div
        class="container pt-10 pb-32 px-4 md:px-6 xl:px-10 xl:pt-14 xl:max-w-screen-xl"
      >
        <slot />
      </div>
    </template>

    <!-- hoist the #sidebar-nav slot-->
    <template #sidebar-nav>
      <slot name="sidebar-nav" />
    </template>

    <!-- hoist the #sidebar-bottom slot, include default, always include toolbox-->
    <template #sidebar-bottom>
      <slot name="sidebar-bottom">
        <div class="space-y-4">
          <div
            v-if="
              currentUser.canAccessMemberContent && currentUser.referralCode
            "
            class="px-1.5"
          >
            <ReferralCodeTile :referral-code="currentUser.referralCode" />
          </div>
        </div>
      </slot>

      <!--SidebarTools - specific features to expose to users for demo, staging, dev etc.-->
      <div v-if="toolbox.length > 0" class="border-t border-gray-100 pt-6">
        <SidebarTools :initial-props="initialProps" :toolbox="toolbox" />
      </div>
    </template>
  </SidebarLayout>
</template>
